import { useState, useCallback } from 'react';
import axiosInstance from '../../../api/axios';
import { Box, Button, Typography, Grid, FormControl, InputLabel, TextField, InputAdornment, IconButton } from '@mui/material';
import { Visibility, VisibilityOff, CheckCircle, Cancel, Save } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import { useTheme } from '@mui/material/styles';
import { styled } from '@mui/material/styles';
import { notifySuccess, notifyError } from "../../../components/common/toast/ToastFunc";

const CustomInput = styled(TextField)(({ theme }) => ({
  'label + &': {
    marginTop: theme.spacing(3),
  },
  '& .MuiInputBase-input': {
    height: "33px",
    borderRadius: 4,
    position: 'relative',
    backgroundColor: 'white',
    fontSize: 16,
    width: '100%',
    padding: '10px 12px',
    '&.Mui-disabled': {
      color: 'black',
      fontWeight: 'bold',
      backgroundColor: '#f2f2f2'
    },
  },
}));

const UpdatePassword = (props) => {

  const { buttonTheme } = props;

  const [formData, setFormData] = useState({ curr_password: '', new_password: '', confirm_new_password: '', show_curr_password: false, show_new_password: false, show_confirm_new_password: false });
  const [updPwdLoading, setUpdPwdLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const theme = useTheme();

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const checkPasswordCriteria = useCallback((password) => {
    const lengthCheck = password.length >= 10;
    const uppercaseCheck = /[A-Z]/.test(password);
    const lowercaseCheck = /[a-z]/.test(password);
    const numberCheck = /\d/.test(password);
    const symbolCheck = /[!@#$%^&*(),.?":{}|<>]/.test(password);
    return lengthCheck && uppercaseCheck && lowercaseCheck && numberCheck && symbolCheck;
  }, []);

  const validateField = useCallback((name, value) => {
    switch (name) {
      case 'curr_password':
        if (value.trim().length === 0) {
          return 'This field is required';
        }
        return '';
      case 'new_password':
        if (value.trim().length === 0) {
          return 'This field is required';
        }
        return checkPasswordCriteria(value.trim()) ? '' : 'Password criteria does not match';
      case 'confirm_new_password':
        if (value.length > 0) {
          return value === formData.new_password ? '' : 'Password does not match';
        }
        if (value.trim().length === 0) {
          return 'This field is required';
        }
        return '';
      default:
        return '';
    }
  }, [checkPasswordCriteria, formData.new_password]);

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData({ ...formData, [name]: value });
    if (errors[name]) {
      setErrors({ ...errors, [name]: '' });
    }
  };

  const handleClearPassword = () => {
    setFormData(prev => (
      Object.keys(prev).reduce((acc, key) => {
        acc[key] = typeof prev[key] === 'boolean' ? false : '';
        return acc;
      }, {})
    ));
    setErrors({});
  };

  const handleUpdatePassword = async (e) => {
    try {
      e.preventDefault();
      let newErrors = {};
      Object.keys(formData).forEach((key) => {
        const error = validateField(key, formData[key]);
        if (error) {
          newErrors[key] = error;
        }
      });

      if (Object.keys(newErrors).length > 0) {
        setErrors(newErrors);
        return;
      }

      setUpdPwdLoading(true);
      const response = await axiosInstance.post('/customer/user-info/update-password', formData);
      notifySuccess(response?.data?.message || "Success");
      handleClearPassword();
    } catch (error) {
      notifyError(error.message || "Something went wrong");
    } finally {
      setUpdPwdLoading(false);
    }
  }

  return (
    <>
      <Typography align="center" sx={{ fontSize: "28px", mt: -2, mb: 2, color: theme.mainColor }}>Update Password</Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={4}>
          <FormControl variant="standard" fullWidth sx={{ mb: 2 }}>
            <InputLabel shrink htmlFor="password-input" sx={{ fontWeight: "bold" }}>
              Current Password
            </InputLabel>
            <CustomInput
              id="curr_password-input"
              name="curr_password"
              value={formData['curr_password']}
              onChange={handleInputChange}
              disabled={updPwdLoading}
              type={formData.confirm_curr_password ? 'text' : 'password'}
              error={!!errors.curr_password}
              helperText={errors.curr_password}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setFormData(prev => ({ ...prev, confirm_curr_password: !prev.confirm_curr_password }))}
                      onMouseDown={handleMouseDownPassword}
                    >
                      {formData.confirm_curr_password ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={4}>
          <FormControl variant="standard" fullWidth sx={{ mb: 2 }}>
            <InputLabel shrink htmlFor="password-input" sx={{ fontWeight: "bold" }}>
              New Password
            </InputLabel>
            <CustomInput
              id="new_password-input"
              name="new_password"
              value={formData['new_password']}
              onChange={handleInputChange}
              disabled={updPwdLoading}
              type={formData.show_new_password ? 'text' : 'password'}
              error={!!errors.new_password}
              helperText={errors.new_password}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setFormData(prev => ({ ...prev, show_new_password: !prev.show_new_password }))}
                      onMouseDown={handleMouseDownPassword}
                    >
                      {formData.show_new_password ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={4}>
          <FormControl variant="standard" fullWidth sx={{ mb: 2 }}>
            <InputLabel shrink htmlFor="password-input" sx={{ fontWeight: "bold" }}>
              Confirm New Password
            </InputLabel>
            <CustomInput
              id="confirm_new_password-input"
              name="confirm_new_password"
              value={formData['confirm_new_password']}
              onChange={handleInputChange}
              disabled={updPwdLoading}
              type={formData.show_confirm_new_password ? 'text' : 'password'}
              error={!!errors.confirm_new_password}
              helperText={errors.confirm_new_password}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setFormData(prev => ({ ...prev, show_confirm_new_password: !prev.show_confirm_new_password }))}
                      onMouseDown={handleMouseDownPassword}
                    >
                      {formData.show_confirm_new_password ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </FormControl>
        </Grid>
        <Grid item xs={12}>
          {formData['new_password'] && (
            <Typography
              variant="body2"
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mt: -2,
                mb: 2,
                fontSize: "16px",
                color: checkPasswordCriteria(formData['new_password']) ? "green" : "red",
              }}
            >
              {checkPasswordCriteria(formData['new_password']) ?
                <CheckCircle sx={{ fontSize: 20, mr: 1, color: "green" }} /> :
                <Cancel sx={{ fontSize: 20, mr: 1, color: "red" }} />}
              At least 10 characters long, a combination of uppercase letters, lowercase letters, numbers, and symbols.
            </Typography>
          )}
        </Grid>
        <Box sx={{ display: "flex", justifyContent: "center", width: '100%' }}>
          <Button onClick={() => handleClearPassword()} startIcon={<Cancel />} disabled={updPwdLoading} sx={{ mr: 1, color: theme.mainColor }}>Cancel</Button>
          <LoadingButton
            onClick={handleUpdatePassword}
            loading={updPwdLoading}
            disabled={updPwdLoading}
            loadingPosition="start"
            startIcon={<Save />}
            variant="contained"
            sx={{ ...buttonTheme }}
          >
            Update
          </LoadingButton>
        </Box>
      </Grid>
    </>
  )
}

export default UpdatePassword